/**
 * useTemplateEditor Hook
 *
 * Manages the state and operations for editing a single template,
 * including sections management, title editing, and save operations.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAccessToken } from '@features/user';
import APIService from '@services/APIService';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import type {
  AdminSection,
  AvailableModule,
  LoadedTemplate,
  ModuleMappings,
  SectionWithMeta,
} from '../types';
import { isPublicTemplate, isUserTemplate } from '../types';

interface UseTemplateEditorProps {
  /** Template to edit */
  template: LoadedTemplate | null;
  /** Available modules for section info */
  availableModules: AvailableModule[];
  /** Module mappings for customizations */
  moduleMappings?: ModuleMappings;
  /** Whether template can be modified */
  canModify: boolean;
  /** Callback when template is saved */
  onSave?: () => Promise<unknown>;
}

interface UseTemplateEditorReturn {
  /** Template name (editable) */
  templateName: string;
  /** Template sections with metadata */
  sections: SectionWithMeta[];
  /** Whether template has unsaved changes */
  hasChanges: boolean;
  /** Whether title is being edited */
  isTitleEditing: boolean;
  /** Whether save is in progress */
  isSaving: boolean;
  /** Last save timestamp */
  lastSaved: Date | null;
  /** Start editing title */
  startEditingTitle: () => void;
  /** Update title */
  updateTitle: (title: string) => void;
  /** Finish editing title (may save asynchronously) */
  finishEditingTitle: () => Promise<void>;
  /** Add sections at index */
  addSections: (modules: AvailableModule[], index?: number) => void;
  /** Remove section */
  removeSection: (moduleId: string) => void;
  /** Reorder sections */
  reorderSections: (startIndex: number, endIndex: number) => void;
  /** Save template */
  save: () => Promise<boolean>;
  /** Reset to original state */
  reset: () => void;
  /** Public description (for public templates) */
  publicDescription: string | null;
  /** Remove module mapping (revert customization) */
  removeModuleMapping: (moduleId: string) => Promise<void>;
}

export const useTemplateEditor = ({
  template,
  availableModules,
  moduleMappings,
  canModify,
  onSave,
}: UseTemplateEditorProps): UseTemplateEditorReturn => {
  const { getAccessToken } = useAccessToken();
  const { template: templateMetrics } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [templateName, setTemplateName] = useState('');
  const [sections, setSections] = useState<AdminSection[]>([]);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [localModuleMappings, setLocalModuleMappings] = useState<ModuleMappings | undefined>(
    moduleMappings,
  );

  // Track original state for change detection
  const originalStateRef = useRef<{ name: string; sections: AdminSection[] } | null>(null);
  const hasTrackedStart = useRef(false);

  // Track pending autosave
  const pendingAutosaveRef = useRef<boolean>(false);

  // Calculate if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (!originalStateRef.current) return false;

    const nameChanged = templateName !== originalStateRef.current.name;
    const sectionsChanged =
      JSON.stringify(sections) !== JSON.stringify(originalStateRef.current.sections);

    return nameChanged || sectionsChanged;
  }, [templateName, sections]);

  // Public description for display
  const publicDescription = useMemo(() => {
    if (template && isPublicTemplate(template)) {
      return template.public_description || null;
    }
    return null;
  }, [template]);

  // Transform sections to include metadata
  const sectionsWithMeta = useMemo((): SectionWithMeta[] => {
    return sections.map(section => {
      const moduleInfo = availableModules.find(m => m.module_id === section.module_id);
      const templateMappings = template
        ? localModuleMappings?.[template.template_id]
        : undefined;
      const isCustomized = templateMappings
        ? !!templateMappings[section.module_id]
        : false;

      return {
        ...section,
        moduleInfo,
        isCustomized,
      };
    });
  }, [sections, availableModules, localModuleMappings, template]);

  // Initialize state from template
  useEffect(() => {
    if (template) {
      setTemplateName(template.template_name);
      setSections(template.sections || []);
      originalStateRef.current = {
        name: template.template_name,
        sections: template.sections || [],
      };
      setLastSaved(null);
      hasTrackedStart.current = false;
    }
  }, [template]);

  // Update local module mappings when prop changes
  useEffect(() => {
    setLocalModuleMappings(moduleMappings);
  }, [moduleMappings]);

  // Track customization start
  useEffect(() => {
    if (!hasTrackedStart.current && template && canModify) {
      templateMetrics.customizationStarted(template.template_id, {
        source: 'existing',
        initial_sections_count: template.sections?.length || 0,
      });
      hasTrackedStart.current = true;
    }
  }, [template, canModify, templateMetrics]);

  // Calculate summary modules (first 3 sections)
  const calculateSummaryModules = useCallback((sectionsList: AdminSection[]): string[] => {
    return sectionsList
      .filter(section => section.is_summary_section)
      .map(section => section.module_id);
  }, []);

  // Update summary section flags (first 3 are summary sections)
  const updateSummaryFlags = useCallback((sectionsList: AdminSection[]): AdminSection[] => {
    return sectionsList.map((section, index) => ({
      ...section,
      is_summary_section: index < 3,
    }));
  }, []);

  // Start editing title
  const startEditingTitle = useCallback(() => {
    if (canModify) {
      setIsTitleEditing(true);
    }
  }, [canModify]);

  // Update title
  const updateTitle = useCallback((title: string) => {
    setTemplateName(title);
  }, []);

  // Save template name to server
  const saveTemplateName = useCallback(
    async (newName: string): Promise<boolean> => {
      if (!template || !canModify || !isUserTemplate(template)) return false;

      const trimmedName = newName.trim();
      // Don't save if empty or unchanged
      if (!trimmedName || trimmedName === originalStateRef.current?.name) {
        return false;
      }

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          enqueueSnackbar('Authentication required', { variant: 'error' });
          return false;
        }

        const response = await APIService.makeAPIPostRequest({
          requestString: '/template/updateUserTemplate',
          accessToken,
          body: {
            template_id: template.template_id,
            updated_fields: {
              template_name: trimmedName,
            },
          },
        });

        if (response.ok) {
          // Update original state to reflect the saved name
          if (originalStateRef.current) {
            originalStateRef.current.name = trimmedName;
          }
          enqueueSnackbar('Template name saved', { variant: 'success' });
          onSave?.();
          return true;
        }

        enqueueSnackbar('Failed to save template name', { variant: 'error' });
        return false;
      } catch (error) {
        console.error('Error saving template name:', error);
        enqueueSnackbar('Failed to save template name', { variant: 'error' });
        return false;
      }
    },
    [template, canModify, getAccessToken, enqueueSnackbar, onSave],
  );

  // Finish editing title - auto-save if name changed
  const finishEditingTitle = useCallback(async () => {
    setIsTitleEditing(false);

    const trimmedName = templateName.trim();
    if (template && trimmedName !== originalStateRef.current?.name) {
      // Track the modification
      templateMetrics.sectionModified(template.template_id, {
        action: 'edit_title',
        section_type: 'template_title',
      });

      // Auto-save the name change
      await saveTemplateName(trimmedName);
    }
  }, [template, templateName, templateMetrics, saveTemplateName]);

  // Add sections
  const addSections = useCallback(
    (modules: AvailableModule[], index?: number) => {
      if (!canModify) return;

      const newSections: AdminSection[] = modules.map(module => ({
        module_id: module.module_id,
        display_name: module.name || module.module_name,
        updatable: false,
        is_summary_section: true,
        model: 'gpt-4',
        temperature: 0.1,
        max_tokens: 2500,
      }));

      setSections(prev => {
        let updated: AdminSection[];
        if (index !== undefined && index !== null) {
          updated = [...prev];
          updated.splice(index, 0, ...newSections);
        } else {
          updated = [...prev, ...newSections];
        }
        return updateSummaryFlags(updated);
      });

      // Track additions
      if (template) {
        modules.forEach(() => {
          templateMetrics.addSection(template.template_id, { section_type: 'module' });
        });
      }

      // Trigger autosave
      pendingAutosaveRef.current = true;
    },
    [canModify, template, templateMetrics, updateSummaryFlags],
  );

  // Remove section
  const removeSection = useCallback(
    (moduleId: string) => {
      if (!canModify) return;

      setSections(prev => {
        const updated = prev.filter(section => section.module_id !== moduleId);
        return updateSummaryFlags(updated);
      });

      // Track removal
      if (template) {
        templateMetrics.removeSection(template.template_id, { section_type: 'module' });
      }

      // Trigger autosave
      pendingAutosaveRef.current = true;
    },
    [canModify, template, templateMetrics, updateSummaryFlags],
  );

  // Reorder sections
  const reorderSections = useCallback(
    (startIndex: number, endIndex: number) => {
      if (!canModify || startIndex === endIndex) return;

      setSections(prev => {
        const result = Array.from(prev);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return updateSummaryFlags(result);
      });

      // Track reorder
      if (template) {
        templateMetrics.sectionModified(template.template_id, {
          action: 'reorder',
          section_position: endIndex,
        });
      }

      // Trigger autosave
      pendingAutosaveRef.current = true;
    },
    [canModify, template, templateMetrics, updateSummaryFlags],
  );

  // Internal save function (used for both manual save and autosave)
  const saveTemplate = useCallback(
    async (
      currentName: string,
      currentSections: AdminSection[],
      options: { silent?: boolean } = {},
    ): Promise<boolean> => {
      if (!template || !canModify || !isUserTemplate(template)) return false;

      setIsSaving(true);

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          if (!options.silent) {
            enqueueSnackbar('Authentication required', { variant: 'error' });
          }
          return false;
        }

        const response = await APIService.makeAPIPostRequest({
          requestString: '/template/updateUserTemplate',
          accessToken,
          body: {
            template_id: template.template_id,
            updated_fields: {
              template_name: currentName,
              sections: currentSections,
              summary_modules: calculateSummaryModules(currentSections),
            },
          },
        });

        if (response.ok) {
          setLastSaved(new Date());
          originalStateRef.current = { name: currentName, sections: currentSections };
          if (!options.silent) {
            enqueueSnackbar('Template saved successfully', { variant: 'success' });
          }
          onSave?.();
          return true;
        }

        if (!options.silent) {
          enqueueSnackbar('Failed to save template', { variant: 'error' });
        }
        return false;
      } catch (error) {
        console.error('Error saving template:', error);
        if (!options.silent) {
          enqueueSnackbar('Failed to save template', { variant: 'error' });
        }
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [template, canModify, getAccessToken, calculateSummaryModules, enqueueSnackbar, onSave],
  );

  // Save template (public API - shows notifications)
  const save = useCallback(async (): Promise<boolean> => {
    return saveTemplate(templateName, sections, { silent: false });
  }, [saveTemplate, templateName, sections]);

  // Autosave when sections change (triggered by pendingAutosaveRef)
  useEffect(() => {
    if (!pendingAutosaveRef.current || !template || !canModify || !isUserTemplate(template)) {
      return;
    }

    // Reset the pending flag
    pendingAutosaveRef.current = false;

    // Perform silent autosave
    void saveTemplate(templateName, sections, { silent: true });
  }, [sections, template, canModify, templateName, saveTemplate]);

  // Reset to original state
  const reset = useCallback(() => {
    if (originalStateRef.current) {
      setTemplateName(originalStateRef.current.name);
      setSections(originalStateRef.current.sections);
    }
  }, []);

  // Remove module mapping (revert customization)
  const removeModuleMapping = useCallback(
    async (moduleId: string) => {
      if (!template) return;

      const accessToken = await getAccessToken();
      if (!accessToken) return;

      const response = await APIService.makeAPIPostRequest({
        requestString: '/template/removeModuleMapping',
        accessToken,
        body: {
          template_id: template.template_id,
          module_id: moduleId,
        },
      });

      if (response.ok) {
        setLocalModuleMappings(prev => {
          if (!prev) return prev;
          const newMappings = { ...prev };
          if (newMappings[template.template_id] && newMappings[template.template_id][moduleId]) {
            // Create new object without the deleted key
            const { [moduleId]: _, ...rest } = newMappings[template.template_id];
            newMappings[template.template_id] = rest;
          }
          return newMappings;
        });
      } else {
        enqueueSnackbar('Failed to remove customization', { variant: 'error' });
      }
    },
    [template, getAccessToken, enqueueSnackbar],
  );

  return {
    templateName,
    sections: sectionsWithMeta,
    hasChanges,
    isTitleEditing,
    isSaving,
    lastSaved,
    startEditingTitle,
    updateTitle,
    finishEditingTitle,
    addSections,
    removeSection,
    reorderSections,
    save,
    reset,
    publicDescription,
    removeModuleMapping,
  };
};
